import { Listbox, Transition } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { FETCH_MY_SCHOOLS, FetchMySchoolsQuery, FetchMySchoolsQueryVariables } from '@edwin-group/cj-graphql';

type SchoolsFilterProps = {
  id?: string;
  selectedSchoolsIds: string[];
  onChange: (schoolIds: string[]) => void;
  showChips?: boolean;
  label?: string;
  className?: string;
  onlyOne?: boolean;
};

export default function SchoolsFilter({
  id,
  selectedSchoolsIds,
  onChange,
  showChips = false,
  label = 'Select Schools',
  className = 'w-60',
  onlyOne = false,
}: SchoolsFilterProps) {
  const { data } = useQuery<FetchMySchoolsQuery, FetchMySchoolsQueryVariables>(FETCH_MY_SCHOOLS, {
    variables: {
      asRegularUser: false,
    },
  });

  const [searchQuery, setSearchQuery] = useState('');

  const schools = data?.fetchMySchools;

  const filteredSchools = useMemo(() => {
    return schools?.filter((school) => school?.name?.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [schools, searchQuery]);

  const selectedSchools = useMemo(() => {
    return schools?.filter((school) => selectedSchoolsIds.includes(school.documentId!));
  }, [schools, selectedSchoolsIds]);

  const selectAllSchools = () => {
    if (!onlyOne) {
      const allSchoolIds = schools?.map((school) => school.documentId!).filter(Boolean) || [];
      onChange(allSchoolIds);
    }
  };

  const unselectAllSchools = () => {
    onChange([]);
  };

  const handleRemoveSchool = (schoolId: string) => {
    onChange(selectedSchoolsIds.filter((id) => id !== schoolId));
  };

  const handleChange = (newSelection: string[] | string) => {
    if (onlyOne) {
      onChange(Array.isArray(newSelection) ? newSelection.slice(-1) : [newSelection]);
    } else {
      onChange(Array.isArray(newSelection) ? newSelection : [newSelection]);
    }
  };

  return (
    <>
      <div className={className} id={id}>
        <Listbox
          value={onlyOne ? selectedSchoolsIds?.[0] : selectedSchoolsIds}
          onChange={handleChange}
          multiple={!onlyOne}>
          {({ open }) => (
            <>
              <Listbox.Button className="relative h-full w-full cursor-pointer rounded-lg border border-[#CFD8DC] bg-white px-3 py-3 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <span className="block truncate text-sm">
                  {selectedSchoolsIds.length === 0
                    ? label
                    : onlyOne
                      ? (selectedSchools?.length || 0) > 0
                        ? selectedSchools?.[0]?.name
                        : 'Select the school'
                      : selectedSchoolsIds.length === schools?.length
                        ? 'All schools'
                        : `${selectedSchoolsIds.length} school${selectedSchoolsIds.length > 1 ? 's' : ''}`}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </span>
              </Listbox.Button>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0">
                <Listbox.Options
                  as="div"
                  className="absolute z-50 mt-1 max-h-60 w-80 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <div className="border-b border-gray-200 p-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Search schools..."
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {!onlyOne && (
                    <div className="flex border-b border-gray-200">
                      <button
                        type="button"
                        onClick={selectAllSchools}
                        className="hover:bg-primary-50 w-1/2 py-2 text-center text-sm text-primary-600">
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={unselectAllSchools}
                        className="hover:bg-primary-50 w-1/2 py-2 text-center text-sm text-primary-600">
                        Unselect All
                      </button>
                    </div>
                  )}
                  {filteredSchools?.map((school) => (
                    <Listbox.Option
                      as="div"
                      key={school.documentId ?? `school-${Math.random()}`}
                      value={school.documentId || ''}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                          active ? 'bg-primary-50 text-primary-900' : 'text-gray-900'
                        }`
                      }>
                      {({ selected }) => (
                        <div className="flex items-center gap-2">
                          <div
                            className={`flex h-4 min-w-4 items-center justify-center rounded ${
                              onlyOne ? 'rounded-full' : ''
                            } border ${selected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'}`}>
                            {selected && (
                              <svg className="h-3 w-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {school.name}
                          </span>
                        </div>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </>
          )}
        </Listbox>
      </div>
      {showChips && selectedSchoolsIds.length > 0 && !onlyOne && (
        <div className="mt-4 flex max-h-[100px] flex-wrap gap-2 overflow-y-auto">
          <AnimatePresence initial={false} mode="popLayout">
            {selectedSchools?.map((school) => (
              <motion.div
                key={school.documentId}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8, y: -10 }}
                transition={{
                  duration: 0.2,
                  layout: {
                    duration: 0.2,
                  },
                }}
                className="flex items-center space-x-2 rounded-full border border-[#EEEEEE] bg-white px-3 py-1 text-sm">
                <span className="text-sh-xxs">{school.name}</span>
                <button
                  type="button"
                  className="!ml-1 text-gray-500 hover:text-gray-700"
                  onClick={() => handleRemoveSchool(school.documentId!)}>
                  <XMarkIcon className="h-6 w-6 text-[#90A4AE]" />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </>
  );
}
